import { apiClient } from './client';
import type { LookupItem } from '@/types/asset';
import type {
  AnnualPillarThreeSettlement,
  PillarThreeProjection,
  PillarThreeYearSettlementResult,
  CreateMobilityAccount,
  DecideExpenseClaim,
  ExpenseClaim,
  ExpenseClaimStatus,
  EmployeeMobilityData,
  MobilityAccount,
  MobilityLedgerEntry,
  PayrollExport,
  RecordCategoryRevaluation,
  UpdateEmployeeMobilityData,
  EmployeeVehicleLease,
  EmployeeVehicleLeasePreview,
  UpsertEmployeeVehicleLease,
  ChangeLeaseEntitledCategory,
  LeaseEntitledCategoryChangePreview,
  VehicleCategoryCode,
  VehicleCategoryTco,
  SetVehicleCategoryTcoSet,
  DirectoryEmployeeSyncResult,
  EmployeeCategorySnapshot,
  MobilityBudgetAdvance,
  ExpenseClaimBudgetImpact,
  CommuteDefaults,
  MobilityLeaverSettlementSimulation,
} from '@/types/mobility';

/** API client for the mobility-budget domain: employees, leases, accounts, claims, payroll, and Pillar 3. */
export const mobilityApi = {
  /**
   * List mobility data for all employees in a given plan year.
   * @param year - Plan year.
   * @returns Employee mobility data snapshots for that year.
   */
  async listEmployeeMobilityData(year: number): Promise<EmployeeMobilityData[]> {
    const { data } = await apiClient.get<EmployeeMobilityData[]>('/api/mobility/employees', {
      params: { year },
    });
    return data;
  },

  /**
   * Record a manual mobility-category revaluation for an employee.
   * @param payload - Employee id plus the revaluation details.
   */
  async recordCategoryRevaluation(payload: RecordCategoryRevaluation): Promise<void> {
    await apiClient.post(
      `/api/mobility/employees/${payload.employeeId}/category-revaluations`,
      payload,
    );
  },

  /**
   * Update an employee's mobility experience/profile fields.
   * @param employeeId - Employee id.
   * @param payload - Fields to update.
   * @returns The updated employee mobility data.
   */
  async updateEmployeeMobilityData(
    employeeId: number,
    payload: UpdateEmployeeMobilityData,
  ): Promise<EmployeeMobilityData> {
    const { data } = await apiClient.put<EmployeeMobilityData>(
      `/api/mobility/employees/${employeeId}/experience`,
      payload,
    );
    return data;
  },

  /**
   * Permanently delete an inactive employee's mobility record.
   * @param employeeId - Employee id.
   */
  async permanentlyDeleteInactiveEmployee(employeeId: number): Promise<void> {
    await apiClient.delete(`/api/mobility/employees/${employeeId}`);
  },

  /**
   * Enable or disable mobility-budget availability for an employee.
   * @param employeeId - Employee id.
   * @param enabled - Whether the mobility budget is available to the employee.
   */
  async updateMobilityBudgetAvailability(employeeId: number, enabled: boolean): Promise<void> {
    await apiClient.put(`/api/mobility/employees/${employeeId}/budget-availability`, { enabled });
  },

  /**
   * Fetch an employee's current vehicle lease, if any.
   * @param employeeId - Employee id.
   * @returns The lease, or null if the employee has none (API returns 204).
   */
  async getEmployeeVehicleLease(employeeId: number): Promise<EmployeeVehicleLease | null> {
    const response = await apiClient.get<EmployeeVehicleLease>(`/api/mobility/employees/${employeeId}/vehicle-lease`);
    return response.status === 204 ? null : response.data;
  },

  /**
   * Fetch an employee's mobility category as of a given reference date.
   * @param employeeId - Employee id.
   * @param referenceDate - ISO date to evaluate the category at.
   * @returns The category snapshot at that date.
   */
  async getEmployeeCategory(employeeId: number, referenceDate: string): Promise<EmployeeCategorySnapshot> {
    const { data } = await apiClient.get<EmployeeCategorySnapshot>(`/api/mobility/employees/${employeeId}/category`, { params: { referenceDate } });
    return data;
  },

  /**
   * Create or replace an employee's vehicle lease.
   * @param employeeId - Employee id.
   * @param payload - Lease details.
   * @returns The saved lease.
   */
  async upsertEmployeeVehicleLease(employeeId: number, payload: UpsertEmployeeVehicleLease): Promise<EmployeeVehicleLease> {
    const { data } = await apiClient.put<EmployeeVehicleLease>(`/api/mobility/employees/${employeeId}/vehicle-lease`, payload);
    return data;
  },

  /**
   * Preview the budget/ledger impact of creating or replacing a vehicle lease, without persisting it.
   * @param employeeId - Employee id.
   * @param payload - Proposed lease details.
   * @returns The projected impact of the lease.
   */
  async previewEmployeeVehicleLease(employeeId: number, payload: UpsertEmployeeVehicleLease): Promise<EmployeeVehicleLeasePreview> {
    const { data } = await apiClient.post<EmployeeVehicleLeasePreview>(`/api/mobility/employees/${employeeId}/vehicle-lease/preview`, payload);
    return data;
  },

  /**
   * Change the entitled vehicle category on an existing lease, effective from a given date.
   * @param employeeId - Employee id.
   * @param leaseId - Lease id.
   * @param newCategory - New entitled vehicle category code.
   * @param effectiveDate - ISO date the change takes effect.
   * @returns The updated lease.
   */
  async changeLeaseEntitledCategory(
    employeeId: number,
    leaseId: number,
    newCategory: VehicleCategoryCode,
    effectiveDate: string,
  ): Promise<EmployeeVehicleLease> {
    const payload: ChangeLeaseEntitledCategory = { newCategory, effectiveDate };
    const { data } = await apiClient.put<EmployeeVehicleLease>(
      `/api/mobility/employees/${employeeId}/leases/${leaseId}/entitled-category`,
      payload,
    );
    return data;
  },

  /**
   * Preview the budget/ledger impact of a lease entitled-category change, without persisting it.
   * @param employeeId - Employee id.
   * @param leaseId - Lease id.
   * @param newCategory - Proposed new entitled vehicle category code.
   * @param effectiveDate - ISO date the change would take effect.
   * @returns The projected impact of the change.
   */
  async previewLeaseEntitledCategoryChange(
    employeeId: number,
    leaseId: number,
    newCategory: VehicleCategoryCode,
    effectiveDate: string,
  ): Promise<LeaseEntitledCategoryChangePreview> {
    const payload: ChangeLeaseEntitledCategory = { newCategory, effectiveDate };
    const { data } = await apiClient.post<LeaseEntitledCategoryChangePreview>(
      `/api/mobility/employees/${employeeId}/leases/${leaseId}/entitled-category/preview`,
      payload,
    );
    return data;
  },

  /**
   * List current total-cost-of-ownership (TCO) values per vehicle category.
   * @param referenceDate - Optional ISO date to evaluate TCO as of (defaults to latest on the server).
   * @returns TCO values per vehicle category.
   */
  async listVehicleCategoryTcos(referenceDate?: string): Promise<VehicleCategoryTco[]> {
    const { data } = await apiClient.get<VehicleCategoryTco[]>('/api/mobility/vehicle-categories/tco', {
      params: referenceDate ? { referenceDate } : undefined,
    });
    return data;
  },

  /**
   * List the full history of vehicle category TCO sets.
   * @returns All historical TCO entries.
   */
  async listVehicleCategoryTcoHistory(): Promise<VehicleCategoryTco[]> {
    const { data } = await apiClient.get<VehicleCategoryTco[]>('/api/mobility/vehicle-categories/tco/history');
    return data;
  },

  /**
   * Publish a new set of vehicle category TCO values.
   * @param payload - The TCO set to apply.
   * @returns The resulting current TCO values per category.
   */
  async setVehicleCategoryTcoSet(payload: SetVehicleCategoryTcoSet): Promise<VehicleCategoryTco[]> {
    const { data } = await apiClient.post<VehicleCategoryTco[]>('/api/mobility/vehicle-categories/tco-sets', payload);
    return data;
  },

  /**
   * Synchronize a batch of Azure AD directory users into mobility employee records.
   * @param users - Directory users (object id, display name, and optional mail/UPN) to sync.
   * @returns A summary of created/matched/skipped employees.
   */
  async synchronizeDirectoryEmployees(users: Array<{ id: string; displayName: string; mail?: string | null; userPrincipalName?: string | null }>): Promise<DirectoryEmployeeSyncResult> {
    const { data } = await apiClient.post<DirectoryEmployeeSyncResult>('/api/mobility/employees/directory-sync', users.map((user) => ({
      objectId: user.id,
      displayName: user.displayName,
      mail: user.mail,
      userPrincipalName: user.userPrincipalName,
    })));
    return data;
  },

  /**
   * Grant a mobility-budget advance to an account. The advance is only fully
   * earned pro-rata by its target release date; a leaver forfeits the unearned portion.
   * @param accountId - Mobility account id.
   * @param amount - Advance amount.
   * @param reason - Reason for granting the advance (audit trail).
   * @returns The created advance record.
   */
  async grantBudgetAdvance(accountId: number, amount: number, reason: string): Promise<MobilityBudgetAdvance> {
    const { data } = await apiClient.post<MobilityBudgetAdvance>(`/api/mobility/accounts/${accountId}/advances`, { amount, reason });
    return data;
  },

  /**
   * Reverse a previously granted mobility-budget advance with an explicit correcting entry.
   * @param accountId - Mobility account id.
   * @param advanceId - Id of the advance to reverse.
   * @param reason - Reason for the reversal (audit trail).
   * @returns The updated advance record.
   */
  async reverseBudgetAdvance(accountId: number, advanceId: number, reason: string): Promise<MobilityBudgetAdvance> {
    const { data } = await apiClient.post<MobilityBudgetAdvance>(`/api/mobility/accounts/${accountId}/advances/${advanceId}/reverse`, { reason });
    return data;
  },

  /**
   * Look up employees for mobility-related autocomplete fields.
   * @param search - Optional search text.
   * @returns Matching employee lookup items.
   */
  async employeeLookup(search?: string): Promise<LookupItem[]> {
    const { data } = await apiClient.get<LookupItem[]>('/api/Assets/lookups/employees', {
      params: search ? { search } : undefined,
    });
    return data;
  },

  /**
   * List mobility accounts for a given plan year.
   * @param year - Plan year.
   * @returns Mobility accounts for that year.
   */
  async listAccounts(year: number): Promise<MobilityAccount[]> {
    const { data } = await apiClient.get<MobilityAccount[]>('/api/mobility/accounts', {
      params: { year },
    });
    return data;
  },

  /**
   * Fetch a single employee's mobility account for a given plan year.
   * @param employeeId - Employee id.
   * @param year - Plan year.
   * @returns The mobility account.
   */
  async getAccount(employeeId: number, year: number): Promise<MobilityAccount> {
    const { data } = await apiClient.get<MobilityAccount>(
      `/api/mobility/employees/${employeeId}/accounts/${year}`,
    );
    return data;
  },

  /**
   * Create a new mobility account.
   * @param payload - Account creation details.
   * @returns The created account.
   */
  async createAccount(payload: CreateMobilityAccount): Promise<MobilityAccount> {
    const { data } = await apiClient.post<MobilityAccount>('/api/mobility/accounts', payload);
    return data;
  },

  /**
   * Close a mobility account.
   * @param accountId - Mobility account id.
   * @returns The closed account.
   */
  async closeAccount(accountId: number): Promise<MobilityAccount> {
    const { data } = await apiClient.post<MobilityAccount>(`/api/mobility/accounts/${accountId}/close`);
    return data;
  },

  /**
   * Fetch the append-only ledger entries for a mobility account.
   * @param accountId - Mobility account id.
   * @returns Ledger entries, ordered as returned by the API.
   */
  async getLedger(accountId: number): Promise<MobilityLedgerEntry[]> {
    const { data } = await apiClient.get<MobilityLedgerEntry[]>(
      `/api/mobility/accounts/${accountId}/ledger`,
    );
    return data;
  },

  /**
   * List expense claims across all accounts, optionally filtered by status/period.
   * @param status - Optional claim status filter.
   * @param year - Optional year filter.
   * @param month - Optional month filter.
   * @returns Matching expense claims.
   */
  async getClaimQueue(status?: ExpenseClaimStatus, year?: number, month?: number): Promise<ExpenseClaim[]> {
    const { data } = await apiClient.get<ExpenseClaim[]>('/api/mobility/expense-claims', {
      params: { status, year, month },
    });
    return data;
  },

  /**
   * List expense claims for a single mobility account.
   * @param accountId - Mobility account id.
   * @returns Expense claims for the account.
   */
  async getClaimsForAccount(accountId: number): Promise<ExpenseClaim[]> {
    const { data } = await apiClient.get<ExpenseClaim[]>(
      `/api/mobility/accounts/${accountId}/expense-claims`,
    );
    return data;
  },

  /**
   * Approve or reject an expense claim.
   * @param payload - Claim id and the decision.
   * @returns The updated expense claim.
   */
  async decideClaim(payload: DecideExpenseClaim): Promise<ExpenseClaim> {
    const { data } = await apiClient.post<ExpenseClaim>(
      `/api/mobility/expense-claims/${payload.claimId}/decision`,
      payload,
    );
    return data;
  },

  /**
   * List payroll exports for a given year/month.
   * @param year - Payroll year.
   * @param month - Payroll month.
   * @returns Payroll export records.
   */
  async listPayroll(year: number, month: number): Promise<PayrollExport[]> {
    const { data } = await apiClient.get<PayrollExport[]>('/api/mobility/payroll', {
      params: { year, month },
    });
    return data;
  },

  /**
   * Generate a non-persisted preview of the next payroll export.
   * @returns The preview payroll export.
   */
  async generatePayrollPreview(): Promise<PayrollExport> {
    const { data } = await apiClient.post<PayrollExport>('/api/mobility/payroll/preview');
    return data;
  },

  /**
   * Finalize (persist) a payroll export for the given claims.
   * @param claimIds - Ids of the expense claims to include in the export.
   * @returns The finalized payroll export.
   */
  async finalizePayroll(claimIds: number[]): Promise<PayrollExport> {
    const { data } = await apiClient.post<PayrollExport>('/api/mobility/payroll/final', { claimIds });
    return data;
  },

  /**
   * Run the annual Pillar 3 settlement for a mobility account.
   * @param accountId - Mobility account id.
   * @returns The resulting settlement.
   */
  async settlePillarThree(accountId: number): Promise<AnnualPillarThreeSettlement> {
    const { data } = await apiClient.post<AnnualPillarThreeSettlement>(
      `/api/mobility/accounts/${accountId}/pillar-three-settlement`,
    );
    return data;
  },

  /**
   * Fetch the existing Pillar 3 settlement for a mobility account, if any.
   * @param accountId - Mobility account id.
   * @returns The settlement.
   */
  async getPillarThreeSettlement(accountId: number): Promise<AnnualPillarThreeSettlement> {
    const { data } = await apiClient.get<AnnualPillarThreeSettlement>(
      `/api/mobility/accounts/${accountId}/pillar-three-settlement`,
    );
    return data;
  },

  /**
   * Fetch the projected mobility-budget impact of a single expense claim.
   * @param claimId - Expense claim id.
   * @returns The projected budget impact.
   */
  async getClaimBudgetImpact(claimId: number): Promise<ExpenseClaimBudgetImpact> {
    const { data } = await apiClient.get<ExpenseClaimBudgetImpact>(
      `/api/mobility/expense-claims/${claimId}/budget-impact`,
    );
    return data;
  },

  /**
   * Fetch the values to prefill a woon-werk claim's review form with: the distance of the employee's
   * previous approved claim with the same addresses, and the most recently approved price per km.
   * @param claimId - Expense claim id.
   * @returns The suggested distance and rate (null when there is nothing to take them from).
   */
  async getCommuteDefaults(claimId: number): Promise<CommuteDefaults> {
    const { data } = await apiClient.get<CommuteDefaults>(
      `/api/mobility/expense-claims/${claimId}/commute-defaults`,
    );
    return data;
  },

  /**
   * Reverse a finalized payroll export with an explicit correcting entry.
   * @param exportId - Payroll export id.
   * @param reason - Reason for the reversal (audit trail).
   * @returns The updated payroll export.
   */
  async reversePayroll(exportId: number, reason: string): Promise<PayrollExport> {
    const { data } = await apiClient.post<PayrollExport>(`/api/mobility/payroll/${exportId}/reverse`, { reason });
    return data;
  },

  /**
   * Download the binary content of an expense claim attachment.
   * @param claimId - Expense claim id.
   * @param attachmentId - Attachment id.
   * @returns The attachment content as a Blob.
   */
  async getClaimAttachment(claimId: number, attachmentId: number): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      `/api/mobility/expense-claims/${claimId}/attachments/${attachmentId}/content`,
      { responseType: 'blob' },
    );
    return data;
  },

  /**
   * Fetch the yearly Pillar 3 overview/projection across employees.
   * @param year - Plan year.
   * @returns Per-employee Pillar 3 projections.
   */
  async getPillarThreeYearOverview(year: number): Promise<PillarThreeProjection[]> {
    const { data } = await apiClient.get<PillarThreeProjection[]>('/api/mobility/pillar-three', { params: { year } });
    return data;
  },

  /**
   * Settle Pillar 3 for an entire plan year across all applicable accounts.
   * @param year - Plan year to settle.
   * @returns A summary of the year settlement result.
   */
  async settlePillarThreeYear(year: number): Promise<PillarThreeYearSettlementResult> {
    const { data } = await apiClient.post<PillarThreeYearSettlementResult>('/api/mobility/pillar-three/settle-year', null, { params: { year } });
    return data;
  },

  /**
   * Simulate the financial settlement for an employee leaving on a given date,
   * without persisting anything.
   * @param accountId - Mobility account id.
   * @param departureDate - ISO date of departure.
   * @returns The simulated leaver settlement.
   */
  async getLeaverSettlementSimulation(accountId: number, departureDate: string): Promise<MobilityLeaverSettlementSimulation> {
    const { data } = await apiClient.get<MobilityLeaverSettlementSimulation>(
      `/api/mobility/accounts/${accountId}/leaver-settlement-simulation`,
      { params: { departureDate } },
    );
    return data;
  },
};

